import React, { useEffect, useState } from 'react'
import {
	Box,
	Title,
	TextInput,
	Button,
	Group,
	Divider,
	Table,
	Grid,
	Checkbox,
	Select,
} from '@mantine/core'
import { useParams } from 'react-router-dom'
import {
	getExperience,
	getItemOptions,
	getStationOptions,
	updateExperience,
	addItemOption,
	addStationOption,
	deleteItemOption,
	updateItemOption,
} from '../services/actions/experienceActions'
import ItemOptionModal from '../components/ItemOptionModal'

const EditExperienceDetailsPage = () => {
	const { experienceSlug } = useParams()

	const [name, setName] = useState('')
	const [status, setStatus] = useState('active')
	const [allowStaffAddItems, setAllowStaffAddItems] = useState(false)
	const [allowStaffAddCheckIns, setAllowStaffAddCheckIns] = useState(false)
	const [allowCheckInMassImport, setAllowCheckInMassImport] = useState(false)
	const [sendCheckInConfirmEmails, setSendCheckInConfirmEmails] =
		useState(false)
	const [itemOptions, setItemOptions] = useState([])
	const [stationOptions, setStationOptions] = useState([])
	const [newStationName, setNewStationName] = useState('')
	const [editingItem, setEditingItem] = useState(null)
	const [showItemModal, setShowItemModal] = useState(false)

	useEffect(() => {
		const fetchExperience = async () => {
			const exp = await getExperience(experienceSlug)
			setName(exp.name)
			setStatus(exp.status ?? 'active')
			setAllowStaffAddItems(exp.allowStaffAddItems || false)
			setAllowStaffAddCheckIns(exp.allowStaffAddCheckIns || false)
			setAllowCheckInMassImport(exp.allowCheckInMassImport || false)
			setSendCheckInConfirmEmails(exp.sendCheckInConfirmEmails || false)

			const [items, stations] = await Promise.all([
				getItemOptions(experienceSlug),
				getStationOptions(experienceSlug),
			])

			setItemOptions(items)
			setStationOptions(stations)
		}

		fetchExperience()
	}, [experienceSlug])

	const handleSave = async () => {
		await updateExperience(experienceSlug, {
			name,
			status,
			allowStaffAddItems,
			allowCheckInMassImport,
			allowStaffAddCheckIns,
			sendCheckInConfirmEmails,
		})
		alert('Experience updated')
	}

	const handleAddStation = async () => {
		if (!newStationName) return
		const res = await addStationOption(experienceSlug, newStationName)
		setStationOptions([...stationOptions, res])
		setNewStationName('')
	}

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				Edit Experience Details
			</Title>

			<TextInput
				label='Experience Name'
				value={name}
				onChange={(e) => setName(e.target.value)}
				mb='sm'
			/>

			<Select
				label='Status'
				value={status}
				onChange={setStatus}
				defaultValue='active'
				data={[
					{ value: 'active', label: 'Active' },
					{ value: 'inactive', label: 'Inactive' },
				]}
				mb='md'
			/>

			<Checkbox
				label='Allow staff to add Purchased Items'
				checked={allowStaffAddItems}
				onChange={(e) => setAllowStaffAddItems(e.currentTarget.checked)}
				mb='md'
			/>

			<Checkbox
				label='Allow staff to add new Check-Ins'
				checked={allowStaffAddCheckIns}
				onChange={(e) => setAllowStaffAddCheckIns(e.currentTarget.checked)}
				mb='md'
			/>

			<Checkbox
				label='Allow Check-Ins mass upload'
				checked={allowCheckInMassImport}
				onChange={(e) => setAllowCheckInMassImport(e.currentTarget.checked)}
				mb='md'
			/>

			<Checkbox
				label='Send Check-In email confirmations'
				checked={sendCheckInConfirmEmails}
				onChange={(e) => setSendCheckInConfirmEmails(e.currentTarget.checked)}
				mb='md'
			/>

			<Button onClick={handleSave}>Save</Button>

			<Divider my='xl' />

			<Grid gutter='xl'>
				<Grid.Col xs={12} md={6}>
					<Title order={4} mb='sm'>
						Station Options
					</Title>
					<Table withBorder>
						<tbody>
							{stationOptions.map((opt) => (
								<tr key={opt.id}>
									<td>{opt.name}</td>
								</tr>
							))}
						</tbody>
					</Table>

					<TextInput
						placeholder='New station name'
						value={newStationName}
						onChange={(e) => setNewStationName(e.target.value)}
						mt='sm'
					/>

					<Button mt='sm' onClick={handleAddStation}>
						Add Station
					</Button>
				</Grid.Col>

				<Grid.Col xs={12} md={6}>
					<Title order={4} mb='sm'>
						Item Options
					</Title>
					<Table withBorder>
						<tbody>
							{itemOptions.map((opt) => (
								<tr key={opt.id}>
									<td>{opt.name}</td>
									<td style={{ textAlign: 'right' }}>
										<Group spacing='xs' position='right'>
											<Button
												variant='outline'
												size='xs'
												onClick={() => {
													setEditingItem(opt)
													setShowItemModal(true)
												}}
											>
												Edit
											</Button>
											<Button
												variant='outline'
												color='red'
												size='xs'
												onClick={async () => {
													if (window.confirm('Delete this item?')) {
														await deleteItemOption(experienceSlug, opt.id)
														setItemOptions((prev) =>
															prev.filter((i) => i.id !== opt.id)
														)
													}
												}}
											>
												Delete
											</Button>
										</Group>
									</td>
								</tr>
							))}
						</tbody>
					</Table>

					<Button mt='sm' onClick={() => setShowItemModal(true)}>
						➕ Add Item Option
					</Button>
				</Grid.Col>
			</Grid>

			<ItemOptionModal
				show={showItemModal}
				onClose={() => {
					setShowItemModal(false)
					setEditingItem(null)
				}}
				experienceSlug={experienceSlug}
				item={editingItem}
				onAdd={async (newItem) => {
					if (editingItem) {
						const updated = await updateItemOption(
							experienceSlug,
							editingItem.id,
							newItem
						)
						setItemOptions((prev) =>
							prev.map((item) => (item.id === updated.id ? updated : item))
						)
					} else {
						const added = await addItemOption(experienceSlug, newItem)
						setItemOptions((prev) => [...prev, added])
					}

					setEditingItem(null)
					setShowItemModal(false)
				}}
			/>
		</Box>
	)
}

export default EditExperienceDetailsPage
